import type { FormattedExecutionResult, GraphQLFormattedError } from "graphql";

import {
  graphQLResultHasProtocolErrors,
  PROTOCOL_ERRORS_SYMBOL,
} from "../../errors/index.js";
import type { NetworkError } from "../../errors/index.js";
import { Observable } from "../../utilities/index.js";
import type { Operation, FetchResult, NextLink } from "../core/index.js";
import { ApolloLink } from "../core/index.js";

export interface ErrorResponse {
  /**
   * Errors returned in the `errors` property of the GraphQL response.
   *
   * @deprecated `graphQLErrors` will no longer available in options in Apollo Client 4.0.
   * This value is safe to use in Apollo Client 3.x.
   *
   * **Recommended now**
   *
   * No action needed
   *
   * **When upgrading**
   *
   * `graphQLErrors` has been consolidated to the `error` property. You will need to
   * read the error from the `error` property.
   */
  graphQLErrors?: ReadonlyArray<GraphQLFormattedError>;
  /**
   * Errors thrown during a network request. This is usually an error thrown
   * during a `fetch` call or an error while parsing the response from the
   * network.
   *
   * @deprecated `networkError` will no longer available in options in Apollo Client 4.0.
   * This value is safe to use in Apollo Client 3.x.
   *
   * **Recommended now**
   *
   * No action needed
   *
   * **When upgrading**
   *
   * `networkError` has been consolidated to the `error` property. You will need to
   * read the error from the `error` property.
   */
  networkError?: NetworkError;
  /**
   * Fatal transport-level errors from multipart subscriptions.
   * See the [multipart subscription protocol](https://www.apollographql.com/docs/graphos/routing/operations/subscriptions/multipart-protocol#message-and-error-format) for more information.
   *
   * @deprecated `protocolErrors` will no longer available in options in Apollo Client 4.0.
   * This value is safe to use in Apollo Client 3.x.
   *
   * **Recommended now**
   *
   * No action needed
   *
   * **When upgrading**
   *
   * `protocolErrors` has been consolidated to the `error` property. You will need to
   * read the error from the `error` property.
   */
  protocolErrors?: ReadonlyArray<GraphQLFormattedError>;

  /**
   * @deprecated `response` has renamed to `result` in Apollo Client 4.0. This
   * property is safe to use in Apollo Client 3.x.
   *
   * **Recommended now**
   *
   * No action needed
   *
   * **When migrating**
   *
   * Use the `result` property instead of `response` inside your callback function.
   */
  response?: FormattedExecutionResult;
  operation: Operation;
  forward: NextLink;
}

export namespace ErrorLink {
  /**
   * Callback to be triggered when an error occurs within the link stack.
   */
  export interface ErrorHandler {
    (error: ErrorResponse): Observable<FetchResult> | void;
  }
}

// For backwards compatibility.
export import ErrorHandler = ErrorLink.ErrorHandler;

export function onError(errorHandler: ErrorHandler): ApolloLink {
  return new ApolloLink((operation, forward) => {
    return new Observable((observer) => {
      let sub: any;
      let retriedSub: any;
      let retriedResult: any;

      try {
        sub = forward(operation).subscribe({
          next: (result) => {
            if (result.errors) {
              retriedResult = errorHandler({
                graphQLErrors: result.errors,
                response: result,
                operation,
                forward,
              });
            } else if (graphQLResultHasProtocolErrors(result)) {
              retriedResult = errorHandler({
                protocolErrors: result.extensions[PROTOCOL_ERRORS_SYMBOL],
                response: result,
                operation,
                forward,
              });
            }

            if (retriedResult) {
              retriedSub = retriedResult.subscribe({
                next: observer.next.bind(observer),
                error: observer.error.bind(observer),
                complete: observer.complete.bind(observer),
              });
              return;
            }

            observer.next(result);
          },
          error: (networkError) => {
            retriedResult = errorHandler({
              operation,
              networkError,
              //Network errors can return GraphQL errors on for example a 403
              graphQLErrors:
                (networkError &&
                  networkError.result &&
                  networkError.result.errors) ||
                void 0,
              forward,
            });
            if (retriedResult) {
              retriedSub = retriedResult.subscribe({
                next: observer.next.bind(observer),
                error: observer.error.bind(observer),
                complete: observer.complete.bind(observer),
              });
              return;
            }
            observer.error(networkError);
          },
          complete: () => {
            // disable the previous sub from calling complete on observable
            // if retry is in flight.
            if (!retriedResult) {
              observer.complete.bind(observer)();
            }
          },
        });
      } catch (e) {
        errorHandler({ networkError: e as Error, operation, forward });
        observer.error(e);
      }

      return () => {
        if (sub) sub.unsubscribe();
        if (retriedSub) sub.unsubscribe();
      };
    });
  });
}

export class ErrorLink extends ApolloLink {
  private link: ApolloLink;
  constructor(errorHandler: ErrorLink.ErrorHandler) {
    super();
    this.link = onError(errorHandler);
  }

  public request(
    operation: Operation,
    forward: NextLink
  ): Observable<FetchResult> | null {
    return this.link.request(operation, forward);
  }
}
